// The kit's SettingsEditor (design/kit/components/SettingsEditor): the wide slide-over (ed560) a
// Settings row opens, with the title, the fields, and the sticky footer ("Unsaved changes" left,
// Cancel and Save right). Leaving with unsaved changes asks "Discard changes to [name]?". History
// and Undo come with History (Activity log, later); every save is already logged with before and
// after.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { useModalLayer } from '../shell/modal-layer';
import { AlertTriangleIcon, XIcon } from '../shell/icons';
import { editorCopy } from './settings-editor-lines';

/** The red error line (`errl`) from the Input card. */
export function ErrorLine({ id, text }: { id?: string; text: string }) {
  return (
    <p id={id} className="errl" role="alert">
      <AlertTriangleIcon />
      {text}
    </p>
  );
}

type Props = {
  /** Prefix for the ids of the title and the form. */
  id: string;
  title: string;
  sub: string;
  dirty: boolean;
  /** The fields hold a value the server takes. */
  valid: boolean;
  busy: boolean;
  /** Why the last save did not go through. */
  error: string | null;
  onSave: () => void;
  onClose: () => void;
  children: ReactNode;
};

export function SettingsEditor({
  id,
  title,
  sub,
  dirty,
  valid,
  busy,
  error,
  onSave,
  onClose,
  children,
}: Props) {
  const [confirm, setConfirm] = useState(false);
  const layerRef = useRef<HTMLElement>(null);
  useModalLayer(layerRef);
  const c = editorCopy;

  const requestClose = useCallback(() => {
    if (dirty) setConfirm(true);
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  return (
    <>
      <button
        type="button"
        className="scrim"
        aria-label={`Close ${title}`}
        onClick={requestClose}
      />
      <aside
        ref={layerRef}
        className="panel ed560"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
      >
        <div className="p-head">
          <div className="col g4">
            <h2 id={`${id}-title`} className="dtitle">
              {title}
            </h2>
            <span className="t12 muted">{sub}</span>
          </div>
          <button type="button" className="xbtn" aria-label="Close" onClick={requestClose}>
            <XIcon />
          </button>
        </div>
        <form
          className="p-body"
          id={`${id}-form`}
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty && valid && !busy) onSave();
          }}
        >
          {children}
          {error && <ErrorLine text={error} />}
        </form>
        <div className="p-foot">
          {dirty && <span className="t12 amber w5">{c.unsaved}</span>}
          <span className="ml-auto"></span>
          <button type="button" className="btn" onClick={requestClose}>
            {c.cancel}
          </button>
          <button
            type="submit"
            form={`${id}-form`}
            className="btn btn-primary"
            disabled={!dirty || !valid || busy}
          >
            {c.save}
          </button>
        </div>
      </aside>
      {confirm && (
        <ConfirmPopup
          title={c.discardTitle(title)}
          line={c.discardLine(title)}
          action={c.discard}
          cancel={c.keepEditing}
          danger
          onConfirm={onClose}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
}
